import { computed, inject, Injectable, resource } from '@angular/core';

import { AuthenticationContextService } from '@softline/auth';
import { DateService, Id, isDefined } from '@softline/core';
import { FavoriteRepository } from './favorite.repository';
import { Favorite, FavoriteList } from '@softapps/core';

@Injectable({providedIn: 'root'})
export class FavoriteService {
  private readonly authService = inject(AuthenticationContextService);
  private readonly repository = inject(FavoriteRepository);
  private readonly dateService = inject(DateService);

  private readonly _resource = resource({
    defaultValue: {},
    params: () => ({
      userId: this.authService.userId()
    }),
    loader: async (param) => {
      if(!isDefined(param.params.userId))
        return {};
      return await this.repository.load({ userId: param.params.userId });
    }
  })

  favorites = <T>(key: string) => computed(() => {
    const value = this._resource.value()[key];
    return (value?.favorites ?? []) as Favorite<T>[];
  })

  async add<T>(key: string, id: Id, object: T): Promise<Favorite<T>> {
    const userId = this.authService.userId();
    if(!userId)
      throw new Error('[FavoriteService] add: userId is not defined');

    const list: FavoriteList<T> = this._resource.value()[key] ?? {key, favorites: []};
    const timestamp = this.dateService.now();
    let favorites: Favorite<T>[] = [...list.favorites];
    const favorite = { id, object, timestamp };
    favorites = [favorite, ...favorites];

    await this.repository.saveItem(key, {key, favorites}, { userId });
    return favorite;
  }

  async remove<T>(key: string, id: Id): Promise<Favorite<T>> {
    const userId = this.authService.userId();
    if(!userId)
      throw new Error('[FavoriteService] remove: userId is not defined');

    const list: FavoriteList<T> = this._resource.value()[key] ?? {key, favorites: []};
    let favorites: Favorite<T>[] = [...list.favorites];
    const index = list.favorites.findIndex(fav => fav.id === id);
    if(index === -1)
      throw new Error(`[FavoriteService] remove: Favorite with id ${id} not found`);
    const favorite = favorites[index];
    favorites.splice(index, 1);

    await this.repository.saveItem(key, {key, favorites}, { userId });
    return favorite;
  }

  async clear<T>(key: string): Promise<Favorite<T>[]> {
    const userId = this.authService.userId();
    if(!userId)
      throw new Error('[FavoriteService] clear: userId is not defined');
    const result = await this.repository.deleteItem(key, { userId });
    return result.favorites;
  }
}
